<template>
    <button-base v-bind="$props" :should-target-blank="shouldTargetBlank">
        <button :type="type" :disabled="loading || disabled"
                :class="[buttonSize, colorClasses, buttonTextColor, buttonBlock, buttonCursor]"
                class="items-center border border-transparent rounded-md transition shadow-sm focus:outline-none focus:ring-2"
        >
            <icon :name="icon" v-if="icon && !loading" class="h-4 w-4"
                  :class="{'-ml-0.5 mr-2': !!$slots.default || !!text}"
            />
            <loading-spinner v-else-if="loading" class="h-4 w-4" :class="{'-ml-0.5 mr-2': !!$slots.default || !!text}"/>
            <slot>
                {{ text }}
            </slot>
        </button>
    </button-base>
</template>

<script>
import LoadingSpinner from '../Element/LoadingSpinner';
import ButtonBase from '@/Components/Button/Partial/ButtonBase';

export default {
    name: 'default-button',
    components: {ButtonBase, LoadingSpinner},

    props: {
        type: {
            type: String,
            default: 'button',
        },
        icon: {
            type: String,
        },
        size: {
            type: String,
            default: 'md',
        },
        color: {
            type: String,
            default: 'indigo',
        },
        loading: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        block: {
            type: Boolean,
            default: false,
        },
        href: {
            type: String,
            default: null,
        },
        isInertia: {
            type: Boolean,
            default: true,
        },
        shouldTargetBlank: {
            type: Boolean,
            default: false,
        },
        label: {
            type: String,
        },
        text: {
            type: String,
        },
    },

    computed: {
        buttonBlock() {
            return this.block ? 'w-full flex justify-center' : 'inline-flex';
        },
        buttonTextColor() {
            if (this.loading || this.disabled) {
                return `text-${this.color}-300 dark:text-${this.color}-600`;
            }

            if (this.color === 'white') {
                return 'text-white dark:text-gray-600';
            }
            return 'text-white';
        },
        colorClasses() {
            switch (this.color) {
                case 'gray':
                    return (this.loading ? 'bg-gray-700 text-gray-300 ' : 'bg-gray-600 text-white ') +
                        'hover:bg-gray-700 focus:bg-gray-900 focus:ring-gray-500 active:bg-gray-900';
                case 'red':
                    return (this.loading ? 'bg-red-700 text-red-300 ' : 'bg-red-600 text-white ') +
                        'hover:bg-red-700 focus:bg-red-900 focus:ring-red-500 active:bg-red-900';
                case 'green':
                    return (this.loading ? 'bg-green-700 text-green-300 ' : 'bg-green-600 text-white ') +
                        'hover:bg-green-700 focus:bg-green-900 focus:ring-green-500 active:bg-green-900';
                case 'cyan':
                    return (this.loading ? 'bg-cyan-700 text-cyan-300 ' : 'bg-cyan-600 text-white ') +
                        'hover:bg-cyan-700 focus:bg-cyan-900 focus:ring-cyan-500 active:bg-cyan-900';
                case 'orange':
                    return (this.loading ? 'bg-orange-700 text-orange-300 ' : 'bg-orange-600 text-white ') +
                        'hover:bg-orange-700 focus:bg-orange-900 focus:ring-orange-500 active:bg-orange-900';
                case 'pink':
                    return (this.loading ? 'bg-pink-700 text-pink-300 ' : 'bg-pink-600 text-white ') +
                        'hover:bg-pink-700 focus:bg-pink-900 focus:ring-pink-500 active:bg-pink-900';
                case 'rose':
                    return (this.loading ? 'bg-rose-700 text-rose-300 ' : 'bg-rose-600 text-white ') +
                        'hover:bg-rose-700 focus:bg-rose-900 focus:ring-rose-500 active:bg-rose-900';
                case 'blue':
                    return (this.loading ? 'bg-blue-700 text-blue-300 ' : 'bg-blue-600 text-white ') +
                        'hover:bg-blue-700 focus:bg-blue-900 focus:ring-blue-500 active:bg-blue-900';
                case 'indigo':
                    return (this.loading ? 'bg-indigo-700 text-indigo-300 ' : 'bg-indigo-600 text-white ') +
                        'hover:bg-indigo-700 focus:bg-indigo-900 focus:ring-indigo-500 active:bg-indigo-900';
                case 'purple':
                    return (this.loading ? 'bg-purple-700 text-purple-300 ' : 'bg-purple-600 text-white ') +
                        'hover:bg-purple-700 focus:bg-purple-900 focus:ring-purple-500 active:bg-purple-900';
                case 'yellow':
                    return (this.loading ? 'bg-yellow-700 text-yellow-300 ' : 'bg-yellow-600 text-white ') +
                        'hover:bg-yellow-700 focus:bg-yellow-900 focus:ring-yellow-500 active:bg-yellow-900';
                case 'lime':
                    return (this.loading ? 'bg-lime-700 text-lime-300 ' : 'bg-lime-600 text-white ') +
                        'hover:bg-lime-700 focus:bg-lime-900 focus:ring-lime-500 active:bg-lime-900';
                case 'emerald':
                    return (this.loading ? 'bg-emerald-700 text-emerald-300 ' : 'bg-emerald-600 text-white ') +
                        'hover:bg-emerald-700 focus:bg-emerald-900 focus:ring-emerald-500 active:bg-emerald-900';
                case 'teal':
                    return (this.loading ? 'bg-teal-700 text-teal-300 ' : 'bg-teal-600 text-white ') +
                        'hover:bg-teal-700 focus:bg-teal-900 focus:ring-teal-500 active:bg-teal-900';
                case 'white':
                    return (this.loading ? 'bg-white text-gray-800 ' : 'bg-white dark:bg-gray-100 ') +
                        'border-gray-200 hover:text-gray-900 hover:bg-gray-50 dark:border-gray-500 dark:hover:bg-gray-700 dark:hover:text-white focus:ring-gray-200 dark:focus:ring-gray-700';
                default:
                    return (this.loading ? 'bg-gray-700 text-gray-300' : 'bg-gray-600 text-white ') +
                        'hover:bg-gray-700 focus:bg-gray-900 focus:ring-gray-500 active:bg-gray-900';
            }
        },
        buttonCursor() {
            if (this.loading || this.disabled) {
                return ['cursor-default'];
            } else {
                return ['cursor-pointer'];
            }
        },
        buttonSize() {
            switch (this.size) {
                case 'xxs':
                    return 'px-1 py-0.5 text-xxs font-medium';
                case 'xs':
                    return 'px-2 py-1.5 text-xs font-medium';
                case 'sm':
                    return 'px-3 py-2 text-sm font-medium';
                case 'md':
                    return 'px-3 py-2 text-sm font-medium';
                case 'lg':
                    return 'px-3 py-2 text-base font-medium';
                case 'xl':
                    return 'px-4 py-3 text-base font-medium';
                default:
                    return 'px-3 py-2 text-base font-medium';
            }
        },
    },
};
</script>
